/**
 * Bunamo Shared Product Utilities
 * Used by: Coffee Quiz, Flavor Wheel
 */
window.BunamoUtils = (function () {
  'use strict';

  /**
   * Format price in EUR (Shopify cents to EUR string)
   */
  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents, 10);
    }
    var euros = cents / 100;
    return '€' + euros.toFixed(2).replace('.', ',');
  }

  /**
   * Escape HTML for XSS prevention
   */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/`/g, '&#096;');
  }

  /**
   * Create segment bar for metafield values (1-10 scale)
   */
  function createSegmentBar(value, maxSegments) {
    maxSegments = maxSegments || 10;
    var activeCount = value ? parseInt(value) : 0;
    var segments = '';
    for (var i = 1; i <= maxSegments; i++) {
      segments += '<span class="quiz-meta-segment' + (i <= activeCount ? ' active' : '') + '"></span>';
    }
    return segments;
  }

  /**
   * Convert hex color to rgba with alpha
   */
  function hexToRgba(hex, alpha) {
    var r = parseInt(hex.slice(1, 3), 16);
    var g = parseInt(hex.slice(3, 5), 16);
    var b = parseInt(hex.slice(5, 7), 16);
    return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
  }

  /**
   * Render a product card HTML
   * 
   * @param {Object} product - Product object with properties:
   *   - url, title, vendor, price
   *   - image OR featured_image
   *   - tasteNotes (string or array), roastLevel, strength, acidity
   * @param {Object} options - Optional settings:
   *   - showMatchBadge: boolean (default false)
   *   - matchPercent: number (default 0)
   *   - highlightFlavor: string (flavor to highlight in taste notes)
   *   - highlightColor: string (hex color for highlighting, e.g., '#E85D75')
   * @returns {string} HTML string
   */
  function renderProductCard(product, options) {
    options = options || {};
    var showMatchBadge = options.showMatchBadge || false;
    var matchPercent = options.matchPercent || 0;
    var highlightFlavor = options.highlightFlavor || null;
    var highlightColor = options.highlightColor || null;

    var priceFormatted = formatMoney(product.price);
    var imageUrl = product.image || product.featured_image;

    // Handle tasteNotes as string or array
    var tasteNotesText = '';
    if (Array.isArray(product.tasteNotes)) {
      if (highlightFlavor) {
        var normalizedHighlight = highlightFlavor.toLowerCase().trim();
        tasteNotesText = product.tasteNotes.map(function (f) {
          var isHighlighted = String(f).toLowerCase().trim() === normalizedHighlight;
          var highlightStyle = '';
          if (isHighlighted && highlightColor) {
            highlightStyle = ' style="background: ' + hexToRgba(highlightColor, 0.2) + '; color: ' + highlightColor + ';"';
          }
          return '<span class="flavor-item' + (isHighlighted ? ' flavor-item--highlighted' : '') + '"' + highlightStyle + '>' + escapeHtml(f) + '</span>';
        }).join(', ');
      } else {
        tasteNotesText = escapeHtml(product.tasteNotes.join(', '));
      }
    } else if (product.tasteNotes) {
      // String format - add spaces after commas
      tasteNotesText = escapeHtml(String(product.tasteNotes).replace(/,(?!\s)/g, ', '));
    }

    var hasAromas = tasteNotesText.length > 0;
    var hasRoast = product.roastLevel && parseInt(product.roastLevel) > 0;
    var hasStrength = product.strength && parseInt(product.strength) > 0;
    var hasAcidity = product.acidity && parseInt(product.acidity) > 0;

    // Build metafields HTML
    var metafieldsHtml = '<div class="quiz-product-metafields">' +
      '<div class="quiz-product-meta quiz-product-meta-text' + (!hasAromas ? ' disabled' : '') + '">' +
      '<span class="quiz-product-meta-label">Geschmack</span>' +
      '<span class="quiz-product-meta-value">' + (hasAromas ? tasteNotesText : '—') + '</span>' +
      '</div>' +
      '<div class="quiz-product-meta quiz-product-meta-bar' + (!hasRoast ? ' disabled' : '') + '">' +
      '<span class="quiz-product-meta-label">Röstgrad</span>' +
      '<div class="quiz-meta-bar">' + createSegmentBar(product.roastLevel) + '</div>' +
      '</div>' +
      '<div class="quiz-product-meta quiz-product-meta-bar' + (!hasStrength ? ' disabled' : '') + '">' +
      '<span class="quiz-product-meta-label">Stärke</span>' +
      '<div class="quiz-meta-bar">' + createSegmentBar(product.strength) + '</div>' +
      '</div>' +
      '<div class="quiz-product-meta quiz-product-meta-bar' + (!hasAcidity ? ' disabled' : '') + '">' +
      '<span class="quiz-product-meta-label">Säure</span>' +
      '<div class="quiz-meta-bar">' + createSegmentBar(product.acidity) + '</div>' +
      '</div>' +
      '</div>';

    // Match badge (optional)
    var matchBadgeHtml = showMatchBadge ? '<div class="quiz-match-badge">' + matchPercent + '% Match</div>' : '';

    // Build full card HTML
    return '<div class="quiz-product-card">' +
      matchBadgeHtml +
      '<a href="' + product.url + '" class="quiz-product-link">' +
      '<div class="quiz-product-image">' +
      '<img src="' + imageUrl + '" alt="' + escapeHtml(product.title) + '" loading="lazy">' +
      '</div>' +
      '<div class="quiz-product-info">' +
      '<h4 class="quiz-product-title">' + escapeHtml(product.title) + '</h4>' +
      '<p class="quiz-product-vendor">' + escapeHtml(product.vendor) + '</p>' +
      metafieldsHtml +
      '<p class="quiz-product-price">' + priceFormatted + '</p>' +
      '</div>' +
      '</a>' +
      '<a href="' + product.url + '" class="quiz-product-button">Jetzt kaufen</a>' +
      '</div>';
  }

  /**
   * Render pagination controls
   * 
   * @param {HTMLElement} container - Container element for pagination
   * @param {number} currentPage - Current page index (0-based)
   * @param {number} totalPages - Total number of pages
   * @param {Function} onPrev - Callback for previous button
   * @param {Function} onNext - Callback for next button
   */
  function renderPagination(container, currentPage, totalPages, onPrev, onNext, options) {
    if (!container) return;
    
    options = options || {};
    var alwaysShow = options.alwaysShow || false;

    // Hide only if no pages at all and not forced to show
    if (totalPages === 0 && !alwaysShow) {
      container.innerHTML = '';
      container.classList.remove('has-pagination');
      return;
    }

    // Show pagination (with both arrows disabled if only 1 page)
    var prevDisabled = currentPage === 0 || totalPages <= 1;
    var nextDisabled = currentPage >= totalPages - 1 || totalPages <= 1;

    container.classList.add('has-pagination');
    container.innerHTML =
      '<button class="quiz-pagination-arrow quiz-pagination-prev" ' +
      (prevDisabled ? 'disabled' : '') +
      ' aria-label="Vorherige Seite">' +
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<polyline points="15 18 9 12 15 6"></polyline>' +
      '</svg>' +
      '</button>' +
      '<button class="quiz-pagination-arrow quiz-pagination-next" ' +
      (nextDisabled ? 'disabled' : '') +
      ' aria-label="Nächste Seite">' +
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<polyline points="9 18 15 12 9 6"></polyline>' +
      '</svg>' +
      '</button>';

    // Attach event listeners
    var prevBtn = container.querySelector('.quiz-pagination-prev');
    var nextBtn = container.querySelector('.quiz-pagination-next');
    if (prevBtn && onPrev) {
      prevBtn.addEventListener('click', onPrev);
    }
    if (nextBtn && onNext) {
      nextBtn.addEventListener('click', onNext);
    }
  }

  // Public API
  return {
    formatMoney: formatMoney,
    escapeHtml: escapeHtml,
    createSegmentBar: createSegmentBar,
    renderProductCard: renderProductCard,
    renderPagination: renderPagination
  };
})();
